<script lang="ts">
  import { onMount } from 'svelte';
  import { useLocation, useNavigate } from 'svelte-navigator';

  import { ToastQueue, AppState, SetIconsBackground } from '../etc/stores';
  import { Container } from '../services/container';
  import { Auth } from '../services/auth';

  import Page from '../components/misc/Page.svelte';
  import Button from '../components/form/Button.svelte';
  import TextInput from '../components/form/TextInput.svelte';
  import BackArrow from '../components/misc/BackArrow.svelte';
  import { Logger } from '../services/logger';
  import { extractMessageFromError } from '../services/api';
  import { _ } from 'svelte-i18n';
  import { isMobile, validateEmail } from '../etc/utils';
  import { Wix } from '../services/wix';
  import Analytics from '../services/analytics';
  import Heading from '../components/typography/Heading.svelte';
  import Label from '../components/typography/Label.svelte';
  import { Backend } from '../services/backend';

  const t = (text: string) => {
    return $_(`account_name.${text}`);
  };

  const logger = Container.get(Logger).context('AccountName.svelte') as Logger;
  const auth = Container.get(Auth);
  const backend = Container.get(Backend);

  const { providerData = {} } = $AppState.auth;
  const { contactFirstName, contactLastName, contactEmail } =
    $AppState.checkout;

  const { phone } = providerData;

  const navigate = useNavigate();
  const location = useLocation();

  const hideBackButton = $location.state?.hideBackButton;
  const isEditProfile = $location.state?.isEditProfile;

  let firstName = providerData.firstName || contactFirstName || '';
  let lastName = providerData.lastName || contactLastName || '';
  let intialEmail = providerData.email || contactEmail || $location.state.email;
  let email = intialEmail || '';
  let loading = false;
  let input;
  let errorEmail;

  const onClick = async () => {
    try {
      if (!validateEmail(email)) {
        errorEmail = 'Please enter a valid email address.';
        return;
      }
      loading = true;

      if (isEditProfile) {
        await backend.putCustomer({ name: firstName, lastname: lastName });

        AppState.update(state => ({
          ...state,
          customer: {
            ...state.customer,
            name: firstName,
            lastname: lastName
          }
        }));
      } else {
        await auth.signup({
          email,
          firstName,
          lastName,
          phone
        });
      }

      await auth.finishAuthFlow(navigate);
    } catch (e) {
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      loading = false;
    }
    return false;
  };

  onMount(() => {
    if (isMobile()) {
      if (auth.provider === 'wix') {
        Wix.togglePageMenu();
      }
    }
    SetIconsBackground('light');
    Analytics.logPageView(
      `Account Name${Analytics.getFreeCoinsTitleFlow()}`,
      '/account-name'
    );

    setTimeout(() => {
      input.focus();
    }, 300);
  });
</script>

<Page className="connectAccount__container">
  {#if !hideBackButton}
    <BackArrow />
  {/if}
  <div class="connectAccount__title">
    <Heading type="h2" textAlign="center">{t('title')}</Heading>
  </div>
  <div class="connectAccount__description">
    <Label type="default-regular" textAlign="center" color="#7A89B0">
      {t('description')}
    </Label>
  </div>
  <form class="connectAccount__form" on:submit|preventDefault={onClick}>
    <div class="connectAccount__input">
      <TextInput
        bind:input
        bind:value={firstName}
        type="text"
        label={t('first_name')}
        name="name"
        placeholder="Marilyn"
        showCheckOnBlur
      />
    </div>
    <div class="connectAccount__input">
      <TextInput
        bind:value={lastName}
        type="text"
        label={t('last_name')}
        name="lastname"
        placeholder="Monroe"
        showCheckOnBlur
      />
    </div>
    {#if !intialEmail}
      <div class="connectAccount__input">
        <TextInput
          bind:value={email}
          type="email"
          label={t('email')}
          name="email"
          error={errorEmail}
          placeholder="marilyn@mail.com"
        />
      </div>
    {/if}
    <div class="connectAccount__button">
      <Button
        type="submit"
        label={t('btn_done')}
        {loading}
        disabled={!firstName?.length || !lastName?.length || !email?.length}
      />
    </div>
  </form>
</Page>

<style lang="scss">
  div {
    width: 100%;
  }
  .connectAccount {
    &__title {
      margin-top: 64px;
    }
    &__description {
      margin-top: 16px;
    }

    &__input {
      margin-top: 14px;

      &:first-child {
        margin-top: 60px;
      }
    }

    &__button {
      margin-top: 24px;
    }
    &__form {
      width: 100%;
      margin: 0;
      padding: 0;
    }
  }
</style>
